import React, { useState } from 'react';
import { Search, Shield, Menu, X, BarChart3 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import SignalWavesIcon from './SignalWavesIcon';

export default function CyberNav({ onSearch, traceState, onOpenHistory }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isTracing = traceState === 'tracing' || traceState === 'connecting';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchValue.trim() && !isTracing) {
      onSearch(searchValue.trim());
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80 dark:bg-black/60 border-b border-zinc-300 dark:border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Brand */}
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-3 shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            data-testid="header-logo"
          >
            <div className="flex items-center gap-2">
              <SignalWavesIcon className="w-6 h-6 text-cyan-600 dark:text-[#00f0ff]" animated={true} />
              <span className="text-lg font-semibold tracking-tight">
                <span className="text-zinc-900 dark:text-white">whatismyroute</span>
                <span className="text-cyan-600 dark:text-[#00f0ff]">.com</span>
              </span>
            </div>
          </button>

          {/* Search bar - desktop */}
          <form onSubmit={handleSubmit} className="hidden md:flex items-center flex-1 max-w-lg mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 dark:text-white/40" />
              <input
                data-testid="trace-search-input"
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Enter domain or IP (e.g., google.com)"
                className="w-full pl-10 pr-24 py-2.5 bg-zinc-100 dark:bg-white/5 border border-zinc-300 dark:border-white/10 rounded-xl text-sm text-zinc-900 dark:text-white placeholder-zinc-500 dark:placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#00ff9d]/50 dark:focus:ring-[#00ff9d]/50 focus:border-transparent transition-shadow"
                disabled={isTracing}
                aria-label="Trace domain or IP"
              />
              <button
                data-testid="trace-search-submit-button"
                type="submit"
                disabled={isTracing || !searchValue.trim()}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-1.5 bg-emerald-600/10 dark:bg-[#00ff9d]/10 hover:bg-emerald-600/20 dark:hover:bg-[#00ff9d]/20 border border-emerald-600/30 dark:border-[#00ff9d]/30 rounded-lg text-emerald-700 dark:text-[#00ff9d] text-xs font-semibold tracking-wider uppercase transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {isTracing ? 'Tracing...' : 'Trace'}
              </button>
            </div>
          </form>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle />
            <button
              data-testid="open-trace-history-button"
              onClick={onOpenHistory}
              className="flex items-center gap-2 px-3 py-2 text-zinc-600 dark:text-white/60 hover:text-zinc-900 dark:hover:text-white/90 text-sm transition-colors"
            >
              <Shield className="w-4 h-4" />
              History
            </button>
            <button
              data-testid="open-stats-button"
              onClick={() => navigate('/stats')}
              className="flex items-center gap-2 px-3 py-2 text-zinc-600 dark:text-white/60 hover:text-zinc-900 dark:hover:text-white/90 text-sm transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              Stats
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-zinc-600 dark:text-white/60 hover:text-zinc-900 dark:hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-white/5 dark:border-white/5 mt-2 pt-4">
            <form onSubmit={(e) => { handleSubmit(e); setMobileMenuOpen(false); }} className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 dark:text-white/40" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Domain or IP"
                  className="w-full pl-10 pr-20 py-2.5 bg-white/5 dark:bg-white/5 border border-white/10 dark:border-white/10 rounded-xl text-sm text-white dark:text-white placeholder-white/40 dark:placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#00ff9d]/50 dark:focus:ring-[#00ff9d]/50"
                  disabled={isTracing}
                />
                <button
                  type="submit"
                  disabled={isTracing || !searchValue.trim()}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-[#00ff9d]/10 dark:bg-[#00ff9d]/10 border border-[#00ff9d]/30 dark:border-[#00ff9d]/30 rounded-lg text-[#00ff9d] dark:text-[#00ff9d] text-xs font-semibold uppercase disabled:opacity-30"
                >
                  Trace
                </button>
              </div>
            </form>
            <div className="mb-2">
              <ThemeToggle />
            </div>
            <button
              onClick={() => { onOpenHistory(); setMobileMenuOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-white/60 dark:text-white/60 hover:text-white dark:hover:text-white text-sm"
            >
              <Shield className="w-4 h-4" />
              Trace History
            </button>
            <button
              onClick={() => { navigate('/stats'); setMobileMenuOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-white/60 dark:text-white/60 hover:text-white dark:hover:text-white text-sm"
            >
              <BarChart3 className="w-4 h-4" />
              Stats
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
